<template>
    <el-drawer
        class="w-el-drawer"
        :visible.sync="visible"
        :size="720"
        :with-header="false"
        direction="rtl"
        :before-close="close"
        append-to-body
        destroy-on-close>
        <div class="w-el-drawer-header pm-schedule-cell-drawer-header">
            <span class="header-title">
                <i class="el-icon-date"></i>  排班明细 
            </span>
            <i class="el-icon-close header-close" @click="close()"></i>
        </div>
        <div v-loading="loading" class="w-el-drawer-body pm-schedule-cell-drawer">
            <div class="drawer-summary">
                <div class="summary-main">
                    <div class="summary-user">
                        <el-avatar :size="44" class="summary-avatar" :src="user?.avatar">{{ userInitial }}</el-avatar>
                        <div class="summary-user-info">
                            <div class="summary-name">{{ user?.name }}</div>
                            <div class="summary-date">
                                <i class="el-icon-date"></i>
                                {{ formattedDate }}
                            </div>
                        </div>
                    </div>
                    <div class="summary-stats">
                        <div class="stat-item">
                            <div class="stat-value">{{ items.length }}</div>
                            <div class="stat-label">工单数</div>
                        </div>
                        <div class="stat-divider"></div>
                        <div class="stat-item highlight">
                            <div class="stat-value">{{ planTotal }}<span class="stat-unit">P</span></div>
                            <div class="stat-label">计划总量</div>
                        </div>
                        <div class="stat-divider"></div>
                        <div class="stat-item highlight">
                            <div class="stat-value">{{ dayLoadDisplay }}<span class="stat-unit">P</span></div>
                            <div class="stat-label">当日安排</div>
                        </div>
                    </div>
                </div>
            </div>

            <el-empty v-if="!items.length" description="当日暂无工单" :image-size="100" />

            <div v-else class="wo-list">
                <div
                    v-for="(row, index) in items"
                    :key="row.id || index"
                    class="wo-card">
                    <div class="wo-card-head">
                        <div class="wo-card-title">
                            <span class="wo-index">{{ index + 1 }}</span>
                            <el-link type="primary" :underline="false" @click="$refs.woDrawer.Open(row.id)">
                                {{ row.name }}
                            </el-link>
                        </div>
                        <el-tag size="mini" effect="dark" v-bind="getWoStateTag(row.wo_state)">
                            {{ getWoStateTag(row.wo_state).label }}
                        </el-tag>
                    </div>

                    <div v-if="row.project" class="wo-card-project">
                        <i class="el-icon-folder-opened"></i>
                        <el-link type="primary" :underline="false" @click="$refs.projectsDrawer.Open(row.project.id)">
                            {{ row.project.number }} · {{ row.project.name }}
                        </el-link>
                    </div>

                    <div class="wo-card-meta">
                        <div class="meta-item">
                            <i class="el-icon-user"></i>
                            <span class="meta-label">收单人</span>
                            <span class="meta-value">{{ row.user_receive?.name ?? '-' }}</span>
                        </div>
                        <div class="meta-item">
                            <i class="el-icon-s-custom"></i>
                            <span class="meta-label">协同人</span>
                            <span class="meta-value">{{ formatAssistNames(row.user_assist) }}</span>
                        </div>     
                        <div class="meta-item">
                            <i class="el-icon-time"></i>
                            <span class="meta-label">计划周期</span>
                            <span class="meta-value">{{ row.date_start }} 至 {{ row.date_end }}</span>
                        </div>  
                    </div>

                    <div class="wo-card-foot">
                        <span class="load-badge">
                              当日 /  <strong>{{ row.load }}</strong> P
                        </span>
                        <span v-if="row.day_predict" class="predict-text">
                            预估 {{ row.day_predict }} P
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="w-el-drawer-footer">
            <el-button size="medium" @click="close()">取消 / 返回</el-button>
            <el-button size="medium" type="primary" @click="openCreateWo()">新建工单</el-button>
        </div>

        <pm-projects-drawer ref="projectsDrawer" />
        <pm-wo-drawer ref="woDrawer" @onChange="handleWoChange" />
        <pm-wo-create ref="woCreate" @onChange="handleWoChange" />
    </el-drawer>
</template>
<script>
const WO_STATE_MAP = {
    0: { label: '待确认', type: 'warning' },
    1: { label: '进行中', type: '' },
    2: { label: '已完成', type: 'success' },
    3: { label: '已关闭', type: 'info' },
    4: { label: '已暂停', type: 'warning' },
    5: { label: '已取消', type: 'warning' },
}

const DAY_STATUS_ICON = {
    planned: 'el-icon-date icon planned',
    in_progress: 'el-icon-video-play icon in-progress',
    continuing: 'icon continuing',
    complete: 'el-icon-check icon complete',
    abnormal: 'el-icon-warning icon abnormal',
    overtime: 'el-icon-moon-night icon overtime',
}

const WEEKDAY_LABELS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

export default {
    data () {
        return {
            visible: false,
            loading: false,
            user: null,
            date: null,
            day: null,
        }
    },
    computed: {
        items () {
            return this.day?.items ?? []
        },
        planTotal () {
            if (this.day?.plan_total !== undefined && this.day?.plan_total !== null) {
                return Number(this.day.plan_total || 0)
            }
            const predictByWo = {}
            this.items.forEach((item) => {
                if (item.id) {
                    predictByWo[item.id] = Number(item.day_predict || 0)
                }
            })
            return Object.values(predictByWo).reduce((sum, value) => sum + value, 0)
        },
        dayLoadTotal () {
            return this.items.reduce((sum, item) => sum + Number(item.load || 0), 0)
        },
        dayLoadDisplay () {
            return this.formatLoadDisplay(this.dayLoadTotal)
        },

        userInitial () {
            return this.user?.name?.[0] ?? '?'
        },
        formattedDate () {
            if (!this.date) {
                return '-'
            }
            const weekday = WEEKDAY_LABELS[new Date(this.date.replace(/-/g, '/')).getDay()]
            return `${this.date} ${weekday}`
        },
    },
    methods: {
        open ({ user, date, day }) {
            this.user = user
            this.date = date
            this.day = day
            this.visible = true
        },
        refresh ({ user, date, day }) {
            this.user = user
            this.date = date
            this.day = day
        },
        close () {
            this.visible = false
            this.user = null
            this.date = null
            this.day = null
        },
        formatAssistNames (assistUsers) {
            if (!assistUsers?.length) {
                return '-'
            }
            return assistUsers.map((item) => item.name).join('、')
        },
        formatLoadDisplay (value) {
            const num = Number(value)
            if (!Number.isFinite(num)) {
                return '0'
            }
            const rounded = Math.round(num * 100) / 100
            if (Math.abs(num - rounded) > 1e-9) {
                return rounded.toFixed(2).replace(/\.?0+$/, '')
            }
            return parseFloat(num.toPrecision(12)).toString()
        },
        getWoStateTag (state) {
            return WO_STATE_MAP[state] ?? { label: '-', type: 'info' }
        },
        openCreateWo () {
            this.$refs.woCreate.Open()
            if (this.user?.id) {
                this.$refs.woCreate.form.user_receive_id = this.user.id
            }
            if (this.date) {
                this.$refs.woCreate.form.range_at = [this.date, this.date]
            }
        },
        handleWoChange () {
            this.$emit('onChange')
        },
    },
}
</script>
<style scoped>
.pm-schedule-cell-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.header-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.header-close {
    flex-shrink: 0;
    font-size: 16px;
    color: #909399;
    cursor: pointer;
    transition: color 0.2s;
}

.header-close:hover {
    color: #409eff;
}

.pm-schedule-cell-drawer {
    padding: 0;
}

.drawer-summary {
    margin-bottom: 20px;
    border-radius: 10px;
    overflow: hidden;
    background: linear-gradient(135deg, #f0f5ff 0%, #f8fbff 55%, #fff 100%);
    border: 1px solid #dce8ff;
    box-shadow: 0 2px 12px rgba(64, 158, 255, 0.08);
}

.summary-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 16px 20px;
    flex-wrap: wrap;
}

.summary-user {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.summary-avatar {
    flex-shrink: 0;
    background: #409eff;
    color: #fff;
    font-weight: 600;
}

.summary-user-info {
    min-width: 0;
}

.summary-name {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    line-height: 1.3;
}

.summary-date {
    margin-top: 4px;
    font-size: 15px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 4px;
}

.summary-status {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    background: #fff;
    border: 1px solid #ebeef5;
    color: #606266;
}

.summary-status.is-planned {
    color: #409eff;
    border-color: #c6e2ff;
    background: #ecf5ff;
}

.summary-status.is-in_progress,
.summary-status.is-continuing,
.summary-status.is-overtime {
    color: #e6a23c;
    border-color: #f5dab1;
    background: #fdf6ec;
}

.summary-status.is-complete {
    color: #303133;
    border-color: #dcdfe6;
    background: #f4f4f5;
}

.summary-status.is-abnormal {
    color: #f56c6c;
    border-color: #fbc4c4;
    background: #fef0f0;
}

.summary-status .icon {
    font-size: 16px;
    font-style: normal;
}

.summary-status .icon.continuing {
    width: 12px;
    height: 12px;
    border: 2px solid currentColor;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
}

.summary-stats {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: auto;
}

.stat-item {
    flex: 0 0 auto;
    min-width: 72px;
    padding: 0 14px;
    text-align: center;
}

.stat-item.highlight .stat-value {
    color: #409eff;
}

.stat-value {
    font-size: 20px;
    font-weight: 700;
    color: #303133;
    line-height: 1.2;
}

.stat-unit {
    margin-left: 2px;
    font-size: 14px;
    font-weight: 600;
}

.stat-label {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
}

.stat-divider {
    width: 1px;
    height: 32px;
    background: #e4e7ed;
    flex-shrink: 0;
}

.wo-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.wo-card {
    border: 1px solid #ebeef5;
    border-radius: 10px;
    padding: 14px 16px;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.wo-card:hover {
    border-color: #c6e2ff;
    box-shadow: 0 4px 16px rgba(64, 158, 255, 0.1);
}

.wo-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.wo-card-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
}


.wo-index {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: #ecf5ff;
    color: #409eff;
    font-size: 12px;
    font-weight: 600;
    line-height: 22px;
    text-align: center;
}

.wo-card-title ::v-deep .el-link {
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    word-break: break-all;
}

.wo-card-head ::v-deep .el-tag.el-tag--mini {
    height: 22px;
    line-height: 20px;
    padding: 0 8px;
}

.wo-card-project {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding: 8px 10px;
    border-radius: 6px;
    background: #f5f7fa;
    font-size: 12px;
    color: #606266;
}

.wo-card-project ::v-deep .el-link {
    font-size: 12px;
}

.wo-card-meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 16px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #ebeef5;
}

.meta-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
    font-size: 13px;
    line-height: 1.5;
}

.meta-item i {
    flex-shrink: 0;
    color: #c0c4cc;
    font-size: 14px;
    line-height: 1.5;
}

.meta-label {
    flex-shrink: 0;
    color: #909399;
}

.meta-value {
    color: #606266;
    word-break: break-all;
}

.wo-card-meta .meta-item:last-child {
    grid-column: 1 / -1;
}

.wo-card-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 12px;
}

.load-badge {
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    padding: 0px 10px 3px 10px;
    border-radius: 6px;
    background: #ecf5ff;
    color: #409eff;
    font-size: 12px;
}

.load-badge strong {
    font-size: 16px;
    font-weight: 700;
    margin-left: 4px;
}

.predict-text {
    font-size: 14px;
    color: #909399;
}
</style>
